import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { api, loadRazorpay, formatINR, monthLabel } from "../services/mmcapi";

const AdvanceMaintenanceModal = ({ onClose, onPaid }) => {
  const [info, setInfo] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState([]);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api("mmc.getAdvanceInfo");
        if (alive) setInfo(data);
      } catch (e) {
        if (alive) setInfo({ canPay: false, reason: e.message, availableMonths: [] });
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const monthly = Number(info?.monthlyAmount) || 0;
  const total = monthly * selected.length;

  const toggle = (m) =>
    setSelected((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  const close = () => {
    if (paying) return;
    onClose();
  };

  const pay = async () => {
    if (!selected.length || paying) return;
    setPaying(true);
    try {
      await loadRazorpay();
      const order = await api("mmc.createAdvanceOrder", { months: selected });

      const rzp = new window.Razorpay({
        key: order.keyId,
        order_id: order.orderId,
        amount: order.amount,
        currency: order.currency,
        name: "Adukkumadi",
        description: `Advance maintenance - ${order.months.length} month(s)`,
        theme: { color: "#007bff" },
        handler: async (resp) => {
          try {
            await api("mmc.verifyPayment", resp);
            toast.success("Advance payment successful");
          } catch (e) {
            toast.warning("Payment received. Confirmation is in progress, refresh in a moment.");
          }
          setPaying(false);
          onPaid();
        },
        modal: { ondismiss: () => setPaying(false) }
      });
      rzp.on("payment.failed", (r) => {
        toast.error(r?.error?.description || "Payment failed. Please try again.");
        setPaying(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.message);
      setPaying(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16
      }}
      onClick={close}
    >
      <div
        className="card app-modal-card"
        style={{ width: "min(520px, 96vw)", maxHeight: "90vh", overflow: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-header app-modal-header">
          <div className="app-modal-title">Advance Maintenance</div>
          <button
            type="button"
            className="app-modal-close"
            onClick={close}
            disabled={paying}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div className="card-body">
          {!loaded ? (
            <div className="text-muted">Loading...</div>
          ) : !info?.canPay ? (
            <div className="alert alert-warning mb-0">
              {info?.reason || "Advance payment is not available right now."}
            </div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Monthly maintenance</label>
                <input className="form-control" value={formatINR(monthly)} readOnly />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Select months <span className="required-star">*</span>
                </label>
                <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                  {info.availableMonths.map((m) => {
                    const on = selected.includes(m);
                    return (
                      <label
                        key={m}
                        style={{
                          border: `1px solid ${on ? "#007bff" : "#ced4da"}`,
                          background: on ? "rgba(0,123,255,0.08)" : "transparent",
                          borderRadius: 6,
                          padding: "6px 10px",
                          margin: 0,
                          cursor: "pointer",
                          fontSize: 13
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggle(m)}
                          style={{ marginRight: 6 }}
                        />
                        {monthLabel(m)}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="form-group mb-0">
                <label className="form-label">Amount to pay</label>
                <input className="form-control" value={formatINR(total)} readOnly />
                <small className="text-muted">
                  {selected.length} month(s) × {formatINR(monthly)}
                </small>
              </div>
            </>
          )}
        </div>

        <div className="card-footer d-flex justify-content-end" style={{ gap: 10 }}>
          <button type="button" className="btn btn-secondary" onClick={close} disabled={paying}>
            Cancel
          </button>
          {info?.canPay ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={pay}
              disabled={paying || selected.length === 0}
            >
              {paying ? "Processing..." : `Pay ${formatINR(total)}`}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AdvanceMaintenanceModal;